import {
    faChevronDown,
    faChevronRight,
    faCompressArrowsAlt,
    faExpandArrowsAlt,
    faFileInvoiceDollar,
    faFilter,
    faFolder,
    faFolderOpen,
    faRulerHorizontal,
    faSearch,
    faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Badge, Button, Card } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { TextFieldAtom } from "../../../../components/atoms/atom";
import DropdownAtom from "../../../../components/atoms/dropdown/dropdown";
import {
    selectAddedGroupsV2,
    selectCarriedForwardDataV2,
    selectHierarchicalLVDataV2,
    selectPositionsV2,
    selectReadonlyV2,
    selectShowOnlyInvalidPositionsV2,
    setAddedGroups,
    setShowOnlyInvalidPositions,
} from "../../../../store/slices/aufmassV2Slice";
import { formatCurrencyToLocale } from "../../../../utils/method";
import ExpandablePositionsTable from "./local/ExpandablePositionsTable";
import RecursiveGroupRenderer from "./local/RecursiveGroupRenderer";
import { calculateTotals, getAllGroupIds } from "./local/hierarchicalHelpers";
import "./HierarchicalPositionsV2.scss";

const getAmountStatusColor = (measured, invoice) => {
    if (!invoice || invoice === 0) return "#6c757d";
    const diff = Math.abs(measured - invoice);
    if (diff < 0.01) return "#059669";
    if (measured > invoice) return "#dc2626";
    return "#d97706";
};

const HierarchicalPositionsV2 = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const hierarchicalLVData = useSelector(selectHierarchicalLVDataV2);
    const addedGroups = useSelector(selectAddedGroupsV2);
    const positions = useSelector(selectPositionsV2);
    const carriedForwardData = useSelector(selectCarriedForwardDataV2);
    const showOnlyInvalidPositions = useSelector(selectShowOnlyInvalidPositionsV2);
    const isReadonly = useSelector(selectReadonlyV2);

    const lvs = useMemo(() => hierarchicalLVData || [], [hierarchicalLVData]);

    const [searchTerm, setSearchTerm] = useState("");
    const [positionFilter, setPositionFilter] = useState("all");
    const [expandedLVs, setExpandedLVs] = useState(new Set());
    const [expandedGroups, setExpandedGroups] = useState(new Set());
    const [expandedMeasurements, setExpandedMeasurements] = useState(new Set());

    useEffect(() => {
        if (showOnlyInvalidPositions && positionFilter !== "invalid") {
            setPositionFilter("invalid");
        } else if (!showOnlyInvalidPositions && positionFilter === "invalid") {
            setPositionFilter("all");
        }
    }, [showOnlyInvalidPositions, positionFilter]);

    const positionFilterOptions = [
        { value: "all", label: t("hierarchical-positions.all-positions") },
        { value: "new", label: t("hierarchical-positions.new-positions") },
        { value: "carried-forward", label: t("hierarchical-positions.carried-forward-positions") },
        { value: "measured", label: t("aufmass.measured-positions") },
        { value: "missing-measurement", label: t("aufmass.missing-measurement-positions") },
        { value: "invalid", label: t("aufmass.invalid-positions") },
    ];

    const handleMeasurementChange = useCallback((position, measurements) => {
        dispatch(setAddedGroups({ [position.id]: measurements }));
    }, [dispatch]);

    const mergeInvoiceDataIntoPosition = useCallback((lvPosition) => {
        const invoicePosition = positions[lvPosition.id];
        if (!invoicePosition) return lvPosition;
        return {
            ...lvPosition,
            unitPrice: invoicePosition.unitPrice || lvPosition.unitPrice,
            invoiceQuantity: invoicePosition.invoice?.quantity || 0,
            invoiceAmount: invoicePosition.invoice?.amount || 0,
            lv: { unitPrice: lvPosition.unitPrice, quantity: lvPosition.quantity, totalPrice: lvPosition.totalPrice },
            invoice: invoicePosition.invoice,
            allocation: invoicePosition.allocation,
            aufmass: invoicePosition.aufmass,
            isValidated: invoicePosition.isValidated,
            invalidatedAt: invoicePosition.invalidatedAt,
            invalidationReason: invoicePosition.invalidationReason,
            invoicePositionId: invoicePosition.invoicePositionId,
        };
    }, [positions]);

    const mergeInvoiceDataRecursively = useCallback((elements) => {
        if (!elements) return elements;
        return elements.map(el =>
            el.isGroup
                ? { ...el, elements: mergeInvoiceDataRecursively(el.elements) }
                : mergeInvoiceDataIntoPosition(el)
        );
    }, [mergeInvoiceDataIntoPosition]);

    const filterElementsBySearch = useCallback((elements, search, filter) => {
        if (!elements) return elements;
        const term = search?.toLowerCase() || "";

        return elements.filter(element => {
            if (element.isGroup) {
                const kids = element.elements
                    ? filterElementsBySearch(element.elements, search, filter)
                    : [];
                if (kids.length > 0) return true;
                if (filter && filter !== "all") return false;
                return search ? element.name?.toLowerCase().includes(term) : true;
            }

            if (filter && filter !== "all") {
                const posData = positions[element.id];
                const invoicePositionId = posData?.invoicePositionId;
                const hasNew = addedGroups[element.id]?.length > 0;
                const hasCF = invoicePositionId && carriedForwardData[invoicePositionId]?.length > 0;
                const isNewPos = posData?.allocation?.new > 0;

                if (filter === "new" && !isNewPos) return false;
                if (filter === "carried-forward" && !hasCF) return false;
                if (filter === "measured" && !hasNew && !hasCF) return false;
                if (filter === "missing-measurement" && (hasNew || hasCF)) return false;
                if (filter === "invalid" && posData?.isValidated !== false) return false;
                if (showOnlyInvalidPositions && posData?.isValidated !== false) return false;
            }

            if (!search) return true;
            return (
                element.description?.toLowerCase().includes(term) ||
                element.itemNumber?.toString().toLowerCase().includes(term) ||
                element.unit?.toLowerCase().includes(term)
            );
        }).map(el =>
            el.isGroup && el.elements
                ? { ...el, elements: filterElementsBySearch(el.elements, search, filter) }
                : el
        );
    }, [positions, addedGroups, carriedForwardData, showOnlyInvalidPositions]);

    const filteredLVs = useMemo(() => {
        const lvsWithData = lvs.map(lv => ({
            ...lv,
            elements: mergeInvoiceDataRecursively(lv.elements),
        }));
        return lvsWithData.map(lv => ({
            ...lv,
            elements: lv.elements
                ? filterElementsBySearch(lv.elements, searchTerm, positionFilter)
                : [],
        })).filter(lv => {
            const nameMatch = !searchTerm || lv.name?.toLowerCase().includes(searchTerm.toLowerCase());
            return nameMatch || (lv.elements && lv.elements.length > 0);
        }).filter(lv => lv.elements && lv.elements.length > 0);
    }, [lvs, searchTerm, positionFilter, filterElementsBySearch, mergeInvoiceDataRecursively]);

    const calcLVMeasured = useCallback((lv) => {
        const recurse = (el) => {
            if (!el.elements) return 0;
            let t = el.elements.filter(e => !e.isGroup)
                .reduce((s, p) => s + (positions[p.id]?.aufmass?.amount || 0), 0);
            el.elements.filter(e => e.isGroup).forEach(g => { t += recurse(g); });
            return t;
        };
        return recurse(lv);
    }, [positions]);

    const calcLVInvoice = useCallback((lv) => {
        const recurse = (el) => {
            if (!el.elements) return 0;
            let t = el.elements.filter(e => !e.isGroup)
                .reduce((s, p) => s + (positions[p.id]?.invoice?.amount || 0), 0);
            el.elements.filter(e => e.isGroup).forEach(g => { t += recurse(g); });
            return t;
        };
        return recurse(lv);
    }, [positions]);

    const lvTotals = useMemo(() => filteredLVs.reduce((acc, lv) => {
        const t = calculateTotals(lv.elements);
        return { groups: acc.groups + t.groups, positions: acc.positions + t.positions };
    }, { groups: 0, positions: 0 }), [filteredLVs]);

    const totalMeasured = useMemo(() =>
        filteredLVs.reduce((s, lv) => s + calcLVMeasured(lv), 0),
        [filteredLVs, calcLVMeasured]);

    const totalInvoice = useMemo(() =>
        filteredLVs.reduce((s, lv) => s + calcLVInvoice(lv), 0),
        [filteredLVs, calcLVInvoice]);

    const toggleLV = useCallback((id) => {
        setExpandedLVs(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
    }, []);

    const toggleGroup = useCallback((id) => {
        setExpandedGroups(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
    }, []);

    const toggleMeasurement = useCallback((id) => {
        setExpandedMeasurements(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
    }, []);

    const expandAllVisible = useCallback(() => {
        setExpandedLVs(new Set(filteredLVs.map(lv => lv.id)));
        const groups = new Set();
        filteredLVs.forEach(lv => getAllGroupIds(lv.elements).forEach(id => groups.add(id)));
        setExpandedGroups(groups);
    }, [filteredLVs]);

    const expandAll = expandAllVisible;

    const didExpandForInvalidFilter = useRef(false);

    useEffect(() => {
        if (!showOnlyInvalidPositions) {
            didExpandForInvalidFilter.current = false;
            return;
        }
        if (positionFilter !== "invalid" || didExpandForInvalidFilter.current) {
            return;
        }
        if (filteredLVs.length === 0) {
            return;
        }
        expandAllVisible();
        didExpandForInvalidFilter.current = true;
    }, [
        showOnlyInvalidPositions,
        positionFilter,
        filteredLVs,
        expandAllVisible,
    ]);

    const collapseAll = useCallback(() => {
        setExpandedLVs(new Set());
        setExpandedGroups(new Set());
        setExpandedMeasurements(new Set());
    }, []);

    return (
        <div className="positions-panel">
            {/* Toolbar */}
            <div className="positions-toolbar d-flex align-items-center gap-2 flex-wrap px-3 py-2">
                <div className="hp-v2-search">
                    <TextFieldAtom
                        type="text"
                        placeholder={t("hierarchical-positions.search-placeholder")}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        showClearIcon={true}
                        onClear={() => setSearchTerm("")}
                        clearIconTitle={t("hierarchical-positions.clear-search")}
                        suffix={<FontAwesomeIcon icon={faSearch} />}
                        size="sm"
                    />
                </div>
                <div className="hp-v2-filter">
                    <DropdownAtom
                        value={positionFilter}
                        options={positionFilterOptions}
                        onChange={(e) => setPositionFilter(e.target.value)}
                        size="small"
                        isClearable={false}
                        isSearchable={false}
                    />
                </div>
                <div className="d-flex gap-1 flex-shrink-0">
                    <Button variant="outline-secondary" size="sm" onClick={expandAll}
                        title={t("hierarchical-positions.expand-all")}>
                        <FontAwesomeIcon icon={faExpandArrowsAlt} />
                    </Button>
                    <Button variant="outline-secondary" size="sm" onClick={collapseAll}
                        title={t("hierarchical-positions.collapse-all")}>
                        <FontAwesomeIcon icon={faCompressArrowsAlt} />
                    </Button>
                </div>

                {/* Stats + amount pills */}
                <div className="ms-auto flex-shrink-0 d-none d-md-flex align-items-center gap-3">
                    <small className="text-muted">
                        {filteredLVs.length} {t("hierarchical-positions.lvs")} ·{" "}
                        {lvTotals.groups} {t("hierarchical-positions.groups")} ·{" "}
                        {lvTotals.positions} {t("hierarchical-positions.positions")}
                    </small>
                    <div className="aufmass-amount-pill-group">
                        <span className="aufmass-amount-pill invoice">
                            <FontAwesomeIcon icon={faFileInvoiceDollar} className="me-1" />
                            {formatCurrencyToLocale(totalInvoice)}
                        </span>
                        <span
                            className="aufmass-amount-pill measured"
                            style={{ background: getAmountStatusColor(totalMeasured, totalInvoice) }}
                        >
                            <FontAwesomeIcon icon={faRulerHorizontal} className="me-1" />
                            {formatCurrencyToLocale(totalMeasured)}
                        </span>
                    </div>
                </div>
            </div>

            {/* Invalid filter banner */}
            {showOnlyInvalidPositions && (
                <div className="aufmass-filter-banner">
                    <div className="d-flex align-items-center gap-2">
                        <FontAwesomeIcon icon={faFilter} />
                        <span>{t("aufmass.invalid-filter-alert.showing-only-invalid")}</span>
                    </div>
                    <button
                        className="aufmass-filter-clear-btn"
                        onClick={() => dispatch(setShowOnlyInvalidPositions(false))}
                    >
                        <FontAwesomeIcon icon={faTimes} className="me-1" />
                        {t("aufmass.invalid-filter-alert.clear-filter")}
                    </button>
                </div>
            )}

            {/* Scrollable LV list */}
            <div className="positions-body">
                <div className="px-3 pb-3 pt-2">
                    {filteredLVs.length === 0 ? (
                        <div className="text-center py-5">
                            <FontAwesomeIcon icon={faSearch} size="3x" className="text-muted mb-3" />
                            <h5 className="text-muted">{t("hierarchical-positions.no-results-found")}</h5>
                            <p className="text-muted small">
                                {searchTerm
                                    ? `${t("hierarchical-positions.no-results-message")} "${searchTerm}"`
                                    : positionFilter === "measured"
                                        ? t("aufmass.no-measured-positions-message")
                                        : t("hierarchical-positions.no-positions-message")}
                            </p>
                        </div>
                    ) : (
                        filteredLVs.map((lv) => {
                            const isExpanded = expandedLVs.has(lv.id);
                            const lvMeasured = calcLVMeasured(lv);
                            const lvInvoice = calcLVInvoice(lv);
                            const statusColor = getAmountStatusColor(lvMeasured, lvInvoice);

                            return (
                                <Card key={lv.id} className="lv-card mb-3">
                                    <div className="lv-card-header" onClick={() => toggleLV(lv.id)}>
                                        <div className="d-flex align-items-center gap-2">
                                            <div className="lv-expand-icon">
                                                <FontAwesomeIcon
                                                    icon={isExpanded ? faChevronDown : faChevronRight}
                                                />
                                            </div>
                                            <FontAwesomeIcon
                                                icon={isExpanded ? faFolderOpen : faFolder}
                                                className="hp-v2-folder-icon"
                                            />
                                            <span className="lv-name">{lv.name}</span>
                                            {lv.isNachtraege && (
                                                <Badge bg="warning" className="hp-v2-badge-xs">
                                                    {t("hierarchical-positions.nachtraege")}
                                                </Badge>
                                            )}
                                        </div>
                                        <div className="d-flex align-items-center gap-2">
                                            <Badge bg="secondary" className="hp-v2-badge-xs-muted">
                                                {lv.elements?.filter(el => el.isGroup).length ?? 0}{" "}
                                                {t("hierarchical-positions.groups")}
                                            </Badge>
                                            {/* Invoice / measured pill */}
                                            <div className="lv-amount-pills">
                                                <span className="lv-amount-pill invoice">
                                                    <FontAwesomeIcon icon={faFileInvoiceDollar} className="me-1" />
                                                    {formatCurrencyToLocale(lvInvoice)}
                                                </span>
                                                <span
                                                    className="lv-amount-pill measured"
                                                    style={{ background: statusColor }}
                                                >
                                                    <FontAwesomeIcon icon={faRulerHorizontal} className="me-1" />
                                                    {formatCurrencyToLocale(lvMeasured)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {isExpanded && lv.elements && (
                                        <Card.Body className="p-0 hp-v2-card-body-tinted">
                                            {lv.elements.filter(el => el.isGroup).map(group => (
                                                <RecursiveGroupRenderer
                                                    key={group.id}
                                                    element={group}
                                                    level={0}
                                                    expandedGroups={expandedGroups}
                                                    expandedMeasurements={expandedMeasurements}
                                                    toggleGroup={toggleGroup}
                                                    toggleMeasurement={toggleMeasurement}
                                                    onMeasurementChange={handleMeasurementChange}
                                                    isReadonly={isReadonly}
                                                    positionsData={positions}
                                                />
                                            ))}
                                            {lv.elements.filter(el => !el.isGroup).length > 0 && (
                                                <div className="p-3">
                                                    <ExpandablePositionsTable
                                                        positions={lv.elements.filter(el => !el.isGroup)}
                                                        expandedMeasurements={expandedMeasurements}
                                                        toggleMeasurement={toggleMeasurement}
                                                        onMeasurementChange={handleMeasurementChange}
                                                        isReadonly={isReadonly}
                                                        positionsData={positions}
                                                    />
                                                </div>
                                            )}
                                        </Card.Body>
                                    )}
                                </Card>
                            );
                        })
                    )}
                </div>
            </div>
        </div>
    );
};

export default HierarchicalPositionsV2;
